/**
 * ConsentBanner.tsx — WEB-409 P8-F9.
 *
 * Player overlay shown to EU/unknown-region visitors before any analytics
 * fires. The decision is recorded by the caller into the `w2a_consent` cookie
 * (euConsent.writeConsentDecision) which the pixel layer reads (fail-closed).
 *
 * Intentionally self-contained (inline styles, no Ant Design) — it renders on
 * the public player surface, which must stay lightweight and theme-agnostic.
 */
import React from 'react';
import type { ConsentDecision } from '@/utils/analytics/euConsent';
import { getPublicQuizStrings } from '@/utils/publicQuizStrings';
import { safeHref } from '@/utils/isAllowedUrl';

interface ConsentBannerProps {
  onDecision: (decision: ConsentDecision) => void;
  /** WEB-1820: язык воронки — тексты баннера из словаря publicQuizStrings. */
  language?: string | null;
  /**
   * WEB-2145 п.4 — адрес политики конфиденциальности, посчитанный лестницей
   * `utils/analytics/consentPolicyUrl.ts`. Не задан = ссылки нет: пустая
   * ссылка хуже отсутствующей.
   */
  policyUrl?: string;
  /**
   * WEB-2145 п.3 — решение, которое уже принято. Баннер поднимается второй раз
   * значком «передумать» (`components/public/PublicFooter.tsx`), и без этого
   * человек видел бы две одинаковые кнопки, не понимая, что выбрал раньше.
   */
  decision?: ConsentDecision;
}

export function ConsentBanner({
  onDecision,
  language,
  policyUrl,
  decision,
}: ConsentBannerProps) {
  const strings = getPublicQuizStrings(language);
  return (
    <div
      data-testid="consent-banner"
      role="dialog"
      aria-live="polite"
      aria-label={strings.consentAriaLabel}
      style={{
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 2147483000,
        background: 'rgba(17, 17, 17, 0.96)',
        color: '#fff',
        padding: '16px 20px',
        display: 'flex',
        flexWrap: 'wrap',
        gap: 12,
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0 -2px 16px rgba(0,0,0,0.35)',
        fontSize: 14,
        lineHeight: 1.45,
      }}
    >
      <span style={{ maxWidth: 560, flex: '1 1 280px' }}>
        {strings.consentMessage}
        {policyUrl ? (
          <>
            {' '}
            <a
              data-testid="consent-policy-link"
              // WEB-2385 п.1: та же дверь очистки адреса, что у всех
              // ссылок плеера — исполняемая схема схлопывается в '#'.
              href={safeHref(policyUrl)}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#7cb8ff', textDecoration: 'underline' }}
            >
              {strings.consentPolicyLink}
            </a>
          </>
        ) : null}
      </span>
      <div style={{ display: 'flex', gap: 8, flex: '0 0 auto' }}>
        <button
          type="button"
          data-testid="consent-decline"
          aria-pressed={decision === 'denied'}
          onClick={() => onDecision('denied')}
          style={{
            padding: '8px 16px',
            borderRadius: 6,
            border: '1px solid rgba(255,255,255,0.4)',
            background: 'transparent',
            color: '#fff',
            cursor: 'pointer',
            fontSize: 14,
          }}
        >
          {strings.consentDecline}
        </button>
        <button
          type="button"
          data-testid="consent-accept"
          aria-pressed={decision === 'granted'}
          onClick={() => onDecision('granted')}
          style={{
            padding: '8px 16px',
            borderRadius: 6,
            border: 'none',
            background: '#1668dc',
            color: '#fff',
            cursor: 'pointer',
            fontWeight: 600,
            fontSize: 14,
          }}
        >
          {strings.consentAccept}
        </button>
      </div>
    </div>
  );
}
